import { useParams, Link, useNavigate } from 'react-router-dom';
import ContentContainer from '../utils/ContentContainer/ContentContainer';
import PageHeader from '../utils/PageHeader/PageHeader';
import './ViewScript.css';
import { formatPrescribedMedication } from '../../hooks/useFormatting';
import Spinner from '../utils/Spinner/Spinner';
import { Helmet } from 'react-helmet-async';
import { StyledViewScript } from './ViewScript.styled';
import { Prescription, PrescriptionFormValues, prescriptionSchema } from '../../types/firestore';
import { useDocument } from '../../hooks/useDocument';
import { useHandleFetchError } from '../../hooks/useHandleFetchError';
import type { User } from 'firebase/auth';
import { Alert, Button, Checkbox, Modal, Select, Textarea, useMantineTheme } from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';
import { IconStar, IconArrowForward, IconInfoCircle, IconPrinter } from '@tabler/icons-react';
import { AddFavouriteModal } from './AddFavouriteModal';
import { useDisclosure } from '@mantine/hooks';
import {
  createInitialPrescriptionFormValues,
  usePrescriptionInitialValues,
} from '../../context/PrescriptionInitialValuesContext';
import { format } from 'date-fns';
import { DeleteFavouriteModal } from '../Favourites/DeleteFavouriteModal';
import { updateDoc, doc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { db } from '../../firebase/config';
import { logError } from '../../utils/logError';
import { PRESCRIPTIONS_COLLECTION } from '../../entities/collections';
import baseModalClasses from '../../styles/Modal.module.css';

type ViewScriptProps = {
  user: User;
};

type ReprintModalProps = {
  isOpen: boolean;
  onClose: () => void;
  prescription: Prescription;
};

type ReprintFormValues = {
  reason: string;
  note: string;
  hasConfirmedPatientMatch: boolean;
};

function formatPrescriptionDate(prescription: Prescription) {
  const prescribedDate = prescription.prescribedDate.toDate();
  return format(prescribedDate, 'MMM d, yyyy, h:mm a');
}

function formatReprintDate(
  reprintedAt: NonNullable<Prescription['reprintEvents']>[number]['reprintedAt'],
) {
  return format(reprintedAt.toDate(), 'MMM d, yyyy, h:mm a');
}

/**
 * Convert a prescription to the form values expected by the prescription form.
 *
 * NOTE: Many prescription values are not converted to form values. These include:
 * - Patient data (not saved in prescriptions)
 * - Prescriber data (set automatically when writing a prescription)
 * - Practice data (set automatically when writing a prescription)
 * - Misc data (should not include auth info as it's subject to change)
 * - `medication` (needs to be fetched)
 */
export function convertPrescriptionToFormValues(
  prescription: Prescription,
  options: {
    includePrescriptionNumbers?: boolean;
    reprintData?: {
      reason: string;
      note: string;
    };
  } = {},
) {
  const initialPrescriptionFormValues = createInitialPrescriptionFormValues();
  return {
    ...initialPrescriptionFormValues,
    medication: null,
    drugData: {
      activeIngredient: prescription.activeIngredient,
      brandName: prescription.brandName,
      quantity: prescription.quantity,
      repeats: prescription.repeats,
      dosage: prescription.dosage,
      verified: false,
      indications: '',
      substitutePermitted: prescription.isBrandSubstitutionPermitted,
      brandOnly: prescription.isBrandNameOnly,
      includeBrand: prescription.isBrandNameIncluded,
      pbsRx: prescription.isPbsPrescription,
      compounded: prescription.isCompoundingRequired,
      authRequired: prescription.isAuthoritySelected,
      medicationId: prescription.medicationId,
    },
    miscData: {
      ...initialPrescriptionFormValues.miscData,
      scriptID: options.includePrescriptionNumbers ? prescription.scriptId : '',
      authRxNumber: options.includePrescriptionNumbers
        ? prescription.authorityPrescriptionNumber
        : '',
      age: prescription.patientAge ?? '',
      authCode: prescription.authorityCode ?? '',
      justification: prescription.clinicalJustification ?? '',
      prevAuth: prescription.hasPreviouslyReceivedAuthority ?? false,
      date: options.reprintData
        ? format(prescription.prescribedDate.toDate(), 'yyyy-MM-dd')
        : initialPrescriptionFormValues.miscData.date,
    },
    practiceData: options.reprintData
      ? {
          default: prescription.prescriber?.default ?? false,
          phoneNumber: prescription.prescriber?.phoneNumber ?? '',
          practiceName: prescription.prescriber?.practiceName ?? '',
          prefix: prescription.prescriber?.prefix ?? false,
          streetAddress: prescription.prescriber?.streetAddress ?? '',
          subpremise: prescription.prescriber?.subpremise ?? '',
          suburb: prescription.prescriber?.suburb ?? '',
          state: prescription.prescriber?.state ?? '',
          postcode: prescription.prescriber?.postcode ?? '',
        }
      : initialPrescriptionFormValues.practiceData,
    prescriberData: options.reprintData
      ? {
          fullName: prescription.prescriber?.fullName ?? '',
          prescriberNumber: prescription.prescriber?.prescriberNumber ?? '',
          qualifications: prescription.prescriber?.qualifications ?? '',
        }
      : initialPrescriptionFormValues.prescriberData,
    reprintData: {
      isReprint: Boolean(options.reprintData),
      reason: options.reprintData?.reason ?? '',
      note: options.reprintData?.note ?? '',
    },
  } satisfies PrescriptionFormValues;
}

const ReprintModal = ({ isOpen, onClose, prescription }: ReprintModalProps) => {
  const navigate = useNavigate();
  const { setInitialValues } = usePrescriptionInitialValues();

  const form = useForm<ReprintFormValues>({
    initialValues: {
      reason: '',
      note: '',
      hasConfirmedPatientMatch: false,
    },
    validate: {
      reason: isNotEmpty('Please select a reason'),
      note: (value, values) =>
        values.reason === 'Other' && value.trim() === ''
          ? 'Please add a note when selecting Other'
          : null,
      hasConfirmedPatientMatch: (value) =>
        value ? null : 'Please confirm the patient details will match the original prescription',
    },
  });
  const isOtherReason = form.values.reason === 'Other';

  return (
    <Modal title="Reprint original script" onClose={onClose} opened={isOpen}>
      <form
        onSubmit={form.onSubmit((values) => {
          setInitialValues(
            convertPrescriptionToFormValues(prescription, {
              includePrescriptionNumbers: true,
              reprintData: {
                reason: values.reason,
                note: values.reason === 'Other' ? values.note : '',
              },
            }),
          );
          form.reset();
          onClose();
          navigate('/prescription/new?isReprinting=true');
        })}
      >
        <Alert variant="light" icon={<IconInfoCircle />} color="yellow" title="Important">
          This will create a replacement print using the same script number, medication, prescriber,
          practice, PBS details, and prescribed date. Only patient details can be entered again, and
          they must match the original prescription.
        </Alert>

        <Select
          label="Reason"
          mt="md"
          data={[
            { value: 'Lost original', label: 'Lost original' },
            { value: 'Damaged original', label: 'Damaged original' },
            { value: 'Printing issue', label: 'Printing issue' },
            { value: 'Other', label: 'Other' },
          ]}
          {...form.getInputProps('reason')}
        />

        {isOtherReason ? (
          <Textarea label="Note" mt="md" autosize minRows={2} {...form.getInputProps('note')} />
        ) : null}

        <Checkbox
          label="I confirm this is a replacement copy for the original patient."
          mt="md"
          {...form.getInputProps('hasConfirmedPatientMatch', { type: 'checkbox' })}
        />

        <div className={baseModalClasses.buttons}>
          <Button variant="grey" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary">
            Continue to patient details
          </Button>
        </div>
      </form>
    </Modal>
  );
};

const ViewScript = ({ user }: ViewScriptProps) => {
  const { id } = useParams();
  const {
    data: prescription,
    isLoading,
    error,
  } = useDocument(`prescriptions/${id}`, prescriptionSchema);
  useHandleFetchError(error);
  const theme = useMantineTheme();
  const { setInitialValues } = usePrescriptionInitialValues();

  const [
    isAddFavouriteModalVisible,
    { open: openAddFavouriteModal, close: closeAddFavouriteModal },
  ] = useDisclosure(false);

  const [
    isDeleteFavouriteModalVisible,
    { open: openDeleteFavouriteModal, close: closeDeleteFavouriteModal },
  ] = useDisclosure(false);

  const [isReprintModalVisible, { open: openReprintModal, close: closeReprintModal }] =
    useDisclosure(false);

  const deleteFavourite = async (favouriteToRemove: Prescription) => {
    try {
      await updateDoc(doc(db, PRESCRIPTIONS_COLLECTION, favouriteToRemove.scriptId), {
        isFavourite: false,
        favouriteName: null,
      });
      closeDeleteFavouriteModal();
      toast.success('Deleted favourite');
    } catch (err) {
      logError(err);
      toast.error('An error occurred while deleting the favourite');
    }
  };

  return (
    <>
      <Helmet>
        <title>View script · OptomRx</title>
        <meta
          name="description"
          content="View medication and PBS details for the selected script. Patient data is not retained."
        />
      </Helmet>
      <ContentContainer>
        <StyledViewScript>
          <PageHeader title={`Script #${id}`} />
          {isLoading && <Spinner />}
          {prescription && (
            <>
              <div className="Script__info">
                <div className="Script__medication">
                  <div className="Script__title Script__title--medication">Medication details</div>
                  <div className="Script__info--section Script__drug">
                    {formatPrescribedMedication(prescription)}
                  </div>
                  {prescription.isCompoundingRequired && (
                    <div className="Script__info--section Script__compounded">To be compounded</div>
                  )}
                  <div className="Script__info--section Script__substitute">{`${
                    prescription.isBrandSubstitutionPermitted
                      ? 'Brand substitution allowed'
                      : 'Brand substitution not permitted'
                  }`}</div>
                  <div className="Script__info--section Script__dosage">
                    Dosage: {prescription.dosage}
                  </div>
                  <div className="Script__info--section Script__quantity">
                    Quantity: {prescription.quantity}
                  </div>
                  <div className="Script__info--section Script__repeats">
                    Repeats: {prescription.repeats}
                  </div>
                </div>

                <div className="Script__pbs">
                  <div className="Script__title Script__title--pbs">PBS details</div>
                  <div className="Script__info--section Script__pbs">{`${
                    prescription.isPbsPrescription ? 'PBS prescription' : 'Non-PBS prescription'
                  }`}</div>

                  {prescription.pbsSnapshot?.isAuthorityRequired && (
                    <div className="Script__authority">
                      <div className="Script__info--section Script__authCode">
                        Authority code: {prescription.authorityCode}
                      </div>
                      <div className="Script__info--section Script__authNum">
                        Authority Rx No: {prescription.authorityPrescriptionNumber}
                      </div>
                      <div className="Script__info--section Script__indications">
                        Clinical justification for use of item: {prescription.clinicalJustification}
                      </div>
                    </div>
                  )}
                  <div className="Script__info--section Script__date">
                    Prescribed: {formatPrescriptionDate(prescription)}
                  </div>
                </div>
              </div>

              {prescription.reprintEvents && prescription.reprintEvents.length > 0 ? (
                <div className="Script__info Script__reprints">
                  <div className="Script__title Script__title--reprints">Reprint history</div>
                  {prescription.reprintEvents.map((event) => (
                    <div
                      className="Script__info--section Script__reprint"
                      key={`${event.reprintedBy}-${event.reprintedAt.toMillis()}`}
                    >
                      <div className="Script__reprintDate">
                        {formatReprintDate(event.reprintedAt)}
                      </div>
                      <div className="Script__reprintReason">Reason: {event.reason}</div>
                      {event.note ? (
                        <div className="Script__reprintNote">Note: {event.note}</div>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="PrescriberForm__btns">
                <Button
                  component={Link}
                  variant="primary"
                  leftSection={<IconArrowForward width={20} />}
                  to="/prescription/new?isReprescribing=true"
                  onClick={() => setInitialValues(convertPrescriptionToFormValues(prescription))}
                >
                  Re-prescribe
                </Button>

                <Button
                  variant="primary"
                  leftSection={<IconPrinter width={20} />}
                  onClick={openReprintModal}
                >
                  Reprint
                </Button>

                <Button
                  variant="primary"
                  leftSection={
                    <IconStar
                      width={20}
                      color={prescription.isFavourite ? theme.colors.yellow[5] : '#FFFFFF'}
                      fill={prescription.isFavourite ? theme.colors.yellow[5] : 'transparent'}
                    />
                  }
                  onClick={
                    prescription.isFavourite ? openDeleteFavouriteModal : openAddFavouriteModal
                  }
                >
                  {prescription.isFavourite ? 'Remove from favourites' : 'Add to favourites'}
                </Button>
              </div>
            </>
          )}

          {prescription ? (
            <AddFavouriteModal
              isOpen={isAddFavouriteModalVisible}
              onClose={closeAddFavouriteModal}
              user={user}
              prescription={prescription}
            />
          ) : null}
          {prescription ? (
            <DeleteFavouriteModal
              isOpen={isDeleteFavouriteModalVisible}
              onClose={closeDeleteFavouriteModal}
              favourite={prescription}
              onDelete={() => deleteFavourite(prescription)}
            />
          ) : null}
          {prescription ? (
            <ReprintModal
              isOpen={isReprintModalVisible}
              onClose={closeReprintModal}
              prescription={prescription}
            />
          ) : null}
        </StyledViewScript>
      </ContentContainer>
      <p className="bottom-text">
        OptomRx prioritises patient privacy by not storing any personal details. Only medication
        information is retained for review.
      </p>
    </>
  );
};

export default ViewScript;
